// eslint-disable-next-line no-unused-vars
import React, {
    Fragment,
    useEffect,
    useState
} from "react";
import Progress from 'react-bootstrap/ProgressBar';
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { socket } from "@helpers/bootstrap";
import {
    setAlert,
    removeAlert,
    submitUserReport
} from "@redux-action/alert";
import ClientStorage from "../../libraries/ClientStorage";
/** @jsx jsx */
import { jsx, css, keyframes } from '@emotion/react';

function shouldReload(msg) {
    if (msg) {
        return msg.toLowerCase().match(/(reload)|(refresh)/g);
    }
    return false;
}

export const FlashBox = ({
    api_token,
    user_id,
    userFullName,
    setAlert,
    removeAlert,
    alerts,
    submitUserReport
}) => {
    const [state, setState] = useState({ socket_listening: false });
    const [isTop, setIsTop] = useState(true);
    const [isReporting, setIsReporting] = useState(false);
    const [description, setDescription] = useState("");

    const { socket_listening } = state;

    useEffect(() => {
        initIO();

        window.addEventListener("scroll", () => setIsTop(window.scrollY === 0));
        return window.removeEventListener("scroll", null);
    }, []);

    const initIO = async() => {
        if (socket_listening && user_id) {
            return;
        }

        setState({
            ...state,
            socket_listening: true
        });
        socket.on(
            `flash_message_${user_id}`,
            async({ message, type, id, code }) => {
                /*
                code: null
                id: "6019c55976c1c"
                message: "You have logged in successfully. Last login was at: 2021-02-02 21:31:02. There are 3 required videos to watch."
                socket_id: null
                type: "success"
                user_id: 206
             */
                await setAlert(message, type, id, code);
            }
        );
        socket.emit("SET_CLIENT", {
            user_id: user_id,
            api_token: api_token,
            socket_id: socket.id
        });

        // eslint-disable-next-line no-undef
        await new Promise(resolve => setTimeout(resolve, 250));
        ClientStorage().then(() => console.log("[CLIENT STORAGE INITIATED]"));
    };

    const handleSubmitReport = async id => {
        const alert = alerts.find(
            alert => alert.alertId.toString() === id.toString()
        );
        if (alert) {
            const payload = {
                msg: alert.msg,
                description
            };
            await submitUserReport(payload);
            await removeAlert(id);
            setDescription("");
            setIsReporting(false);
        }
    };

    const slideDown = keyframes`
      from {top: 8%;}
      to {top: 10%;}
    `;
    const slideUp = keyframes`
      from {top: 2%;}
      to {top: 1%;}
    `;

    const progressBar = keyframes`
      0% {
        width: 0%;
      }
      100% {
        width: 100%;
      }
      `;
    const sty = css`
         transition: all 350ms;
         margin-bottom: 1em;
         &.message-absolute {
            position: fixed;
            animation: ${slideDown} 1s ease;
            top: 10%;
            left: 0;
            width: 100%;
            z-index: 1051;
        }

        &.message-sticky {
            animation: ${slideUp} 1s ease;
            position: relative;
            top: 1%;
            z-index: 1051;
        }
        .inner-message {
            margin: 0 auto;
            .close-button {
                position: absolute;
                right:1%;
                top: 1%;
                padding: 0.25em;
                cursor: pointer;
                &:hover {
                    color: #fff;
                }
            }
        }
        .progress-bar-animated {
            animation: ${progressBar} 10s linear;
        }
    `;
    return (
        <div css={sty} className={`${isTop ? "message-sticky" : "message-absolute"}`}>
            <div className="container">
                {alerts.map(({ alertId, alertType, msg, closeable }) => (
                    <div
                        key={alertId}
                        className={`inner-message alert alert-${
                            alertType === "error" ?
                                "danger" :
                                alertType === "system" ?
                                    "info" :
                                    alertType
                        } mb-2`}
                        role="alert"
                    >
                        <CloseButton
                            alertId={alertId}
                            removeAlert={removeAlert}
                        />
                        <MessageBox msg={msg} alertType={alertType} />
                        <ReportForm
                            description={description}
                            setDescription={setDescription}
                            isReporting={isReporting}
                        />
                        <ReportButton
                            shouldReload={shouldReload}
                            msg={msg}
                            alertId={alertId}
                            alertType={alertType}
                            closeable={closeable}
                            isReporting={isReporting}
                            setIsReporting={setIsReporting}
                            handleSubmitReport={handleSubmitReport}
                        />
                        <ProgressBar alertType={alertType} />
                    </div>
                ))}
            </div>
        </div>
    );
};

FlashBox.propTypes = {
    user_id: PropTypes.number.isRequired,
    api_token: PropTypes.string.isRequired,
    setAlert: PropTypes.func.isRequired,
    removeAlert: PropTypes.func.isRequired,
    submitUserReport: PropTypes.func.isRequired,
    alerts: PropTypes.array
};

const mapStateToProps = state => ({
    user_id: state.contacts?.contact?.user_id,
    userFullName: state.contacts?.contact?.name,
    api_token: state.contacts?.contact?.user?.api_token,
    alerts: state.alert
});
export default connect(mapStateToProps, {
    setAlert,
    removeAlert,
    submitUserReport
})(FlashBox);

export const MessageBox = ({ msg, alertType }) => {
    return (
        <div>
            {alertType === "system" && (
                <span>
                    <span className={"font-weight-bold"}>
                        System Announcement.
                    </span>
                    <br />
                </span>
            )}
            <span id={"flash-msg-box-" + alertType}>{msg}</span>
        </div>
    );
};

export const ReportForm = ({ isReporting, description, setDescription }) => {
    return isReporting ? (
        <div className={"form-group"}>
            <label htmlFor="description">Please give us a description.</label>
            <textarea
                onChange={event => setDescription(event.target.value)}
                className="form-control"
                name="description"
                value={description}
                rows={3}
                cols={10}
            />
        </div>
    ) : null;
};

export const CloseButton = ({ alertId, removeAlert }) => {
    return (
        <button
            onClick={() => removeAlert(alertId)}
            className={"close close-button"}
            type="button"
        >
            <span aria-hidden="true">&times;</span>
        </button>
    );
};

export const ReportButton = ({
    msg,
    alertId,
    alertType,
    isReporting,
    setIsReporting,
    handleSubmitReport,
    shouldReload
}) => {
    return (
        <div className={"row justify-content-end mb-2"}>
            <div className={"btn-group mr-3"}>
                {!isReporting ? (
                    <Fragment>
                        {alertType !== "success" && (
                            <button
                                onClick={() => setIsReporting(true)}
                                className="btn btn-sm btn-dark"
                            >
                                Report
                            </button>
                        )}
                        {alertType === "system" && shouldReload(msg) && (
                            <button
                                onClick={() => location.reload()}
                                className={"btn btn-ms btn-warning"}
                            >
                                Reload
                            </button>
                        )}
                    </Fragment>
                ) : (
                    <Fragment>
                        <button
                            onClick={() => setIsReporting(false)}
                            className="btn btn-sm btn-primary"
                        >
                            Back
                        </button>
                        <button
                            onClick={() => handleSubmitReport(alertId)}
                            className="btn btn-sm btn-info"
                        >
                            Submit
                        </button>
                    </Fragment>
                )}
            </div>
        </div>
    );
};

export const ProgressBar = ({ alertType }) => {
    if (["error", "system"].includes(alertType)) {
        return null;
    }
    return <Progress variant={alertType} animated now={0} />;
};
